import { Car } from "@/types/fleet";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Settings, Fuel, Calendar, Infinity, Shield, Banknote, CreditCard, Plane, MessageCircle } from "lucide-react";
import { buildViewedCarMessage, buildGeneralWhatsAppLink } from "@/utils/whatsapp";
import { trackClickWhatsApp } from "@/utils/analytics";

interface DASDetailsSheetProps {
  car: Car | null;
  isOpen: boolean;
  onClose: () => void;
  onRentClick: (car: Car) => void;
}

export const DASDetailsSheet = ({ car, isOpen, onClose, onRentClick }: DASDetailsSheetProps) => {
  if (!car) return null;

  const handleQuickWhatsApp = () => {
    trackClickWhatsApp(`details_inquiry_${car.id}`);
    const message = buildViewedCarMessage(car.title);
    window.open(buildGeneralWhatsAppLink(message), '_blank');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-3xl font-heading">{car.title}</SheetTitle>
          <SheetDescription>{car.description}</SheetDescription>
        </SheetHeader>

        {/* Main Image */}
        <div className="mb-6">
          <img 
            src={car.image_hero}
            alt={car.title}
            className="w-full h-64 object-cover rounded-lg"
            loading="lazy"
          />
        </div>

        {/* Price Badge */}
        <div className="mb-6">
          {car.price_per_day_chf > 0 ? (
            <Badge className="bg-gold text-white text-xl px-4 py-2">
              CHF {car.price_per_day_chf}.- / 24 h
            </Badge>
          ) : (
            <Badge className="bg-secondary text-white text-xl px-4 py-2">
              Preis auf Anfrage
            </Badge>
          )}
        </div>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-2 text-gray-700">
            <Users className="h-5 w-5 text-gold" />
            <div>
              <div className="text-xs text-muted-foreground">{typeof car.seats === 'string' ? 'Personen' : 'Sitze'}</div>
              <div className="font-semibold">{car.seats}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Settings className="h-5 w-5 text-gold" />
            <div>
              <div className="text-xs text-muted-foreground">Getriebe</div>
              <div className="font-semibold">{car.transmission}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Fuel className="h-5 w-5 text-gold" />
            <div>
              <div className="text-xs text-muted-foreground">Kraftstoff</div>
              <div className="font-semibold">{car.fuel_type}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Calendar className="h-5 w-5 text-gold" />
            <div>
              <div className="text-xs text-muted-foreground">Baujahr</div>
              <div className="font-semibold">{car.year}</div>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="mb-6">
          <h3 className="font-heading font-bold text-lg mb-3">Im Preis inbegriffen</h3>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Infinity className="h-5 w-5 text-gold mt-0.5" />
              <span className="text-sm text-gray-700">Unbegrenzte Kilometer</span>
            </div>
            <div className="flex items-start gap-2">
              <Shield className="h-5 w-5 text-gold mt-0.5" />
              <span className="text-sm text-gray-700">Vollkasko- und Haftpflichtversicherung (Selbstbehalt CHF 1'000)</span>
            </div>
            <div className="flex items-start gap-2">
              <Banknote className="h-5 w-5 text-gold mt-0.5" />
              <span className="text-sm text-gray-700">Kaution: CHF 300.-</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6 bg-gray-100 p-4 rounded-lg">
          <h3 className="font-heading font-bold text-lg mb-3">Wichtige Hinweise</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <CreditCard className="h-4 w-4 text-gold mt-0.5" />
              <span>Barzahlung kostenfrei, Kartenzahlung +5 CHF Gebühr</span>
            </div>
            <div className="flex items-start gap-2">
              <Plane className="h-4 w-4 text-gold mt-0.5" />
              <span>Grenzübertritt +50 CHF (bitte bei Buchung angeben)</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button 
            className="flex-1 bg-gold hover:bg-gold-hover text-white font-semibold transition-colors duration-200"
            size="lg"
            onClick={() => {
              onRentClick(car);
              onClose();
            }}
          >
            Jetzt buchen
          </Button>
          <Button 
            variant="outline"
            size="lg"
            className="border-2 border-gold text-gold hover:bg-gold hover:text-white font-semibold transition-colors duration-200"
            onClick={handleQuickWhatsApp}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
